import {WorkstreamRow} from 'client/models/WorkstreamRow';

export type HierarchicalWorkstream = {
  workstream: WorkstreamRow;
  depth: number;
  ancestorIds: number[];
};

type OrderedWorkstream = {workstream: WorkstreamRow; order: number};

const sortSiblings = (items: OrderedWorkstream[]) =>
  items.sort(
    (left, right) =>
      left.order - right.order ||
      left.workstream.name.localeCompare(right.workstream.name) ||
      left.workstream.workstream_id - right.workstream.workstream_id
  );

/** Depth-first visitor that lists each workstream once, at the first path that reaches it. */
const depthFirst = (children: Map<number, OrderedWorkstream[]>) => {
  const ordered: HierarchicalWorkstream[] = [];
  const visited = new Set<number>();
  const visit = (item: OrderedWorkstream, depth: number, ancestorIds: number[]) => {
    const id = item.workstream.workstream_id;
    if (visited.has(id)) return;
    visited.add(id);
    ordered.push({workstream: item.workstream, depth, ancestorIds});
    for (const child of sortSiblings(children.get(id) ?? [])) {
      visit(child, depth + 1, [...ancestorIds, id]);
    }
  };
  return {ordered, visited, visit};
};

/** Orders a root's workstreams in depth-first order, each under its first parent, for selectors. */
export const buildWireframeWorkstreamHierarchy = (
  workstreams: WorkstreamRow[],
  rootId: number
): HierarchicalWorkstream[] => {
  const byId = new Map(workstreams.map((workstream) => [workstream.workstream_id, workstream]));
  const children = new Map<number, OrderedWorkstream[]>();
  const roots: OrderedWorkstream[] = [];

  for (const workstream of workstreams) {
    const parents = workstream.parent_workstreams
      .filter((parent) => parent.workstream_id === rootId || byId.has(parent.workstream_id))
      .sort((left, right) => left.order - right.order || left.workstream_id - right.workstream_id);
    const parent = parents[0];
    if (!parent || parent.workstream_id === rootId) {
      roots.push({workstream, order: parent?.order ?? 0});
      continue;
    }
    const siblings = children.get(parent.workstream_id) ?? [];
    siblings.push({workstream, order: parent.order});
    children.set(parent.workstream_id, siblings);
  }

  const {ordered, visited, visit} = depthFirst(children);

  for (const root of sortSiblings(roots)) visit(root, 0, []);
  // Keep disconnected or malformed rows available rather than silently dropping them.
  for (const item of sortSiblings(
    workstreams
      .filter((workstream) => !visited.has(workstream.workstream_id))
      .map((workstream) => ({workstream, order: 0}))
  ))
    visit(item, 0, []);
  return ordered;
};

/** Lists the root's top-level pillars in sibling order; a pillar is a member of itself. */
export const listWireframePillars = (workstreams: WorkstreamRow[], rootId: number) =>
  sortSiblings(
    workstreams
      .filter((workstream) => workstream.pillar_workstream_ids.includes(workstream.workstream_id))
      .map((workstream) => ({
        workstream,
        order: Math.min(
          ...workstream.parent_workstreams
            .filter((parent) => parent.workstream_id === rootId)
            .map((parent) => parent.order)
        ),
      }))
  ).map(({workstream}) => workstream);

/**
 * Orders one pillar's workstreams depth-first, starting with the pillar itself. A workstream
 * shared with other pillars appears in each of them; one reached through several parents inside
 * this pillar appears once, under the first parent in sibling order.
 */
export const buildWireframePillarHierarchy = (
  workstreams: WorkstreamRow[],
  pillarId: number
): HierarchicalWorkstream[] => {
  const members = workstreams.filter((workstream) =>
    workstream.pillar_workstream_ids.includes(pillarId)
  );
  const pillar = members.find((workstream) => workstream.workstream_id === pillarId);
  if (!pillar) return [];
  const children = new Map<number, OrderedWorkstream[]>();
  for (const workstream of members) {
    if (workstream.workstream_id === pillarId) continue;
    for (const parent of workstream.parent_workstreams) {
      const siblings = children.get(parent.workstream_id) ?? [];
      siblings.push({workstream, order: parent.order});
      children.set(parent.workstream_id, siblings);
    }
  }

  const {ordered, visited, visit} = depthFirst(children);

  visit({workstream: pillar, order: 0}, 0, []);
  // Membership comes from the server; keep any member this parent graph does not reach.
  for (const item of sortSiblings(
    members
      .filter((workstream) => !visited.has(workstream.workstream_id))
      .map((workstream) => ({workstream, order: 0}))
  ))
    visit(item, 1, [pillarId]);
  return ordered;
};
